import type { ShuffleRescheduleFilters } from '../build-reschedule-request-body';
import { buildRescheduleRequestBody } from '../build-reschedule-request-body';
import { buildRescheduleConfirmationSteps } from '../build-reschedule-confirmation-steps';
import { RescheduleConfirmationModal } from '../reschedule-confirmation-modal';
import type { RescheduleProgressDTO, RescheduleProgressStatus } from 'src/types/dto/reschedule-progress.dto';
import type { RescheduleSuggestionDTO } from 'src/types/dto/reschedule-suggestion.dto';
import type { ActivityDTO } from 'src/types/dto/activity.dto';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button } from '@mui/material';

import axios, { endpoints } from 'src/utils/axios';
import { isAxiosError } from 'axios';
import { toast } from 'src/components/snackbar';
import { ProcessingProgress } from 'src/components/processing-progress';
import { InspectorRescheduleCard } from 'src/components/inspector-reschedule-card';
import { useRescheduleSuggestionsData } from 'src/components/inspector-reschedule-card/use-reschedule-suggestions-data';
import {
  isRescheduleProgressRunning,
  isRescheduleProgressTerminal,
} from 'src/types/dto/reschedule-progress.dto';
import { groupRescheduleProgressChanges, mergeRescheduleSuggestions } from '../inspector-availability-grid/group-reschedule-progress-changes';
import { InspectorAvailabilityGridSkeletonLoading } from '../inspector-availability-grid/inspector-availability-grid-skeleton-loading';

const RESCHEDULE_POLL_INTERVAL_MS = 1000;
const RESCHEDULE_CANCEL_VERIFY_DELAY_MS = 10;

const rescheduleStartRequests = new Map<string, Promise<RescheduleProgressDTO>>();

const getRescheduleStartRequestKey = (activityId: number, runId: number) =>
  `${activityId}:run:${runId}`;

const startRescheduleGeneration = (
  activityId: number,
  runId: number,
  body: ReturnType<typeof buildRescheduleRequestBody>,
): Promise<RescheduleProgressDTO> => {
  const requestKey = getRescheduleStartRequestKey(activityId, runId);
  const existing = rescheduleStartRequests.get(requestKey);

  if (existing) {
    return existing;
  }

  const request = axios
    .post<RescheduleProgressDTO>(
      endpoints.activities.rescheduleSuggestions(activityId),
      body,
    )
    .then((response) => response.data)
    .finally(() => {
      rescheduleStartRequests.delete(requestKey);
    });

  rescheduleStartRequests.set(requestKey, request);
  return request;
};

const getCancelErrorMessage = (error: unknown, fallback: string): string => {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string | string[] } | undefined;
    if (typeof data?.message === 'string') {
      return data.message;
    }
    if (Array.isArray(data?.message)) {
      return data.message.join(', ');
    }
    if (error.message) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

interface StepResultsProps {
  activityId: number;
  runId: number;
  currentActivity: ActivityDTO;
  filters: ShuffleRescheduleFilters | null;
  resumeProgressId?: number;
  isDeskService?: boolean;
  onClose?: () => void;
  onRescheduleConfirm?: () => void | Promise<void>;
  onRunningChange?: (isRunning: boolean) => void;
  onProgressStopped?: () => void;
  registerStop?: (stop: (() => void) | null) => void;
  onActiveProgressIdChange?: (progressId: number | null) => void;
  onProgressStatusChange?: (status: RescheduleProgressStatus | null) => void;
  onProgressSucceededChange?: (succeeded: number | undefined) => void;
  siteTimezoneId?: string;
  onDismiss?: () => void;
}

export const StepResults = memo(
  ({
    activityId,
    runId,
    currentActivity,
    filters,
    resumeProgressId,
    isDeskService = false,
    onClose,
    onRescheduleConfirm,
    onRunningChange,
    onProgressStopped,
    registerStop,
    onActiveProgressIdChange,
    onProgressStatusChange,
    onProgressSucceededChange,
    siteTimezoneId,
    onDismiss,
  }: StepResultsProps) => {
    const { t } = useTranslation(['shuffle', 'activities']);
    const [progress, setProgress] = useState<RescheduleProgressDTO | null>(null);
    const [suggestions, setSuggestions] = useState<RescheduleSuggestionDTO[]>([]);
    const [pendingSuggestion, setPendingSuggestion] = useState<RescheduleSuggestionDTO | null>(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const pollInFlightRef = useRef(false);
    const cancelledRef = useRef(false);
    const stopInFlightRef = useRef(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollProgressRef = useRef<
      ((rescheduleProgressId: number) => Promise<RescheduleProgressDTO | null>) | null
    >(null);
    const [isLoading, setIsLoading] = useState(true);
    const activeProgressIdRef = useRef<number | undefined>(resumeProgressId);

    useEffect(() => {
      activeProgressIdRef.current = resumeProgressId;
      onActiveProgressIdChange?.(resumeProgressId ?? null);
    }, [onActiveProgressIdChange, resumeProgressId]);

    const setActiveProgressId = useCallback(
      (progressId: number | undefined) => {
        activeProgressIdRef.current = progressId;
        onActiveProgressIdChange?.(progressId ?? null);
      },
      [onActiveProgressIdChange],
    );

    useEffect(() => {
      if (progress) {
        onProgressStatusChange?.(progress.status);
      }
    }, [onProgressStatusChange, progress]);

    useEffect(() => {
      onProgressSucceededChange?.(progress?.succeeded);
    }, [onProgressSucceededChange, progress?.succeeded]);

    const rescheduleBody = useMemo(
      () => buildRescheduleRequestBody(filters, t),
      [filters, t]
    );

    const stopReschedule = useCallback(async () => {
      if (stopInFlightRef.current) {
        return;
      }

      const progressId = activeProgressIdRef.current;
      if (
        !progressId ||
        (progress && !isRescheduleProgressRunning(progress.status))
      ) {
        return;
      }

      stopInFlightRef.current = true;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      try {
        await axios.post(endpoints.activities.cancelRescheduleProgress(progressId));

        await new Promise((resolve) => {
          setTimeout(resolve, RESCHEDULE_CANCEL_VERIFY_DELAY_MS);
        });

        const { data: latestProgress } = await axios.get<RescheduleProgressDTO>(
          endpoints.activities.rescheduleProgress(progressId)
        );

        if (latestProgress.status !== 'cancelled') {
          throw new Error(t('stepResults.cancelNotConfirmed'));
        }

        cancelledRef.current = true;
        setProgress(latestProgress);
        setSuggestions((current) =>
          mergeRescheduleSuggestions(
            current,
            groupRescheduleProgressChanges(latestProgress.changes ?? [])
          )
        );
        setIsLoading(false);
        onRunningChange?.(false);
        onProgressStopped?.();
      } catch (error) {
        toast.error(getCancelErrorMessage(error, t('stepResults.cancelError')));

        if (activeProgressIdRef.current && pollProgressRef.current) {
          intervalRef.current = setInterval(() => {
            const activeProgressId = activeProgressIdRef.current;
            if (activeProgressId) {
              void pollProgressRef.current?.(activeProgressId);
            }
          }, RESCHEDULE_POLL_INTERVAL_MS);
        }
      } finally {
        stopInFlightRef.current = false;
      }
    }, [onProgressStopped, onRunningChange, progress, t]);

    useEffect(() => {
      registerStop?.(() => {
        void stopReschedule();
      });
      return () => registerStop?.(null);
    }, [registerStop, stopReschedule]);

    useEffect(() => {
      cancelledRef.current = false;
      let cancelled = false;

      const pollProgress = async (
        rescheduleProgressId: number,
      ): Promise<RescheduleProgressDTO | null> => {
        if (pollInFlightRef.current || cancelled || cancelledRef.current) {
          return null;
        }

        pollInFlightRef.current = true;
        try {
          const { data } = await axios.get<RescheduleProgressDTO>(
            endpoints.activities.rescheduleProgress(rescheduleProgressId)
          );

          if (cancelled || cancelledRef.current) {
            return null;
          }

          activeProgressIdRef.current = data.id;
          setActiveProgressId(data.id);
          setProgress(data);

          if (data.changes?.length) {
            const incomingChanges = data.changes;
            setSuggestions((current) =>
              mergeRescheduleSuggestions(
                current,
                groupRescheduleProgressChanges(incomingChanges)
              )
            );
          }

          if (isRescheduleProgressTerminal(data.status)) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            setIsLoading(false);
            onRunningChange?.(false);
            if (data.status === 'completed') {
              setProgress({
                ...data,
                completed: data.total,
              });
            }
          }

          return data;
        } catch (error) {
          if (!cancelled && !cancelledRef.current) {
            toast.error(getCancelErrorMessage(error, t('stepResults.error')));
            setIsLoading(false);
            onRunningChange?.(false);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
          return null;
        } finally {
          pollInFlightRef.current = false;
        }
      };

      pollProgressRef.current = pollProgress;

      const startReschedule = async () => {
        setProgress(null);
        setSuggestions([]);
        setIsLoading(true);
        onRunningChange?.(true);

        try {
          if (resumeProgressId) {
            setActiveProgressId(resumeProgressId);
            const initialProgress = await pollProgress(resumeProgressId);

            if (cancelled || cancelledRef.current) {
              return;
            }

            if (
              initialProgress &&
              isRescheduleProgressTerminal(initialProgress.status)
            ) {
              return;
            }

            intervalRef.current = setInterval(() => {
              void pollProgress(resumeProgressId);
            }, RESCHEDULE_POLL_INTERVAL_MS);
            return;
          }

          const data = await startRescheduleGeneration(
            activityId,
            runId,
            rescheduleBody,
          );

          if (cancelled || cancelledRef.current) {
            return;
          }

          activeProgressIdRef.current = data.id;
          setActiveProgressId(data.id);
          setProgress(data);

          if (data.changes?.length) {
            const incomingChanges = data.changes;
            setSuggestions((current) =>
              mergeRescheduleSuggestions(
                current,
                groupRescheduleProgressChanges(incomingChanges)
              )
            );
          }

          if (isRescheduleProgressTerminal(data.status)) {
            setIsLoading(false);
            onRunningChange?.(false);
            return;
          }

          await pollProgress(data.id);

          if (cancelled || cancelledRef.current) {
            return;
          }

          intervalRef.current = setInterval(() => {
            void pollProgress(data.id);
          }, RESCHEDULE_POLL_INTERVAL_MS);
        } catch (error) {
          if (!cancelled && !cancelledRef.current) {
            toast.error(getCancelErrorMessage(error, t('stepResults.error')));
            setIsLoading(false);
            onRunningChange?.(false);
          }
        }
      };

      void startReschedule();

      return () => {
        cancelled = true;
        pollProgressRef.current = null;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }, [activityId, onRunningChange, rescheduleBody, resumeProgressId, runId, setActiveProgressId, t]);

    const confirmationSteps = useMemo(
      () =>
        pendingSuggestion
          ? buildRescheduleConfirmationSteps(pendingSuggestion, currentActivity)
          : [],
      [currentActivity, pendingSuggestion]
    );

    const orderedSuggestions = useRescheduleSuggestionsData(suggestions, currentActivity);

    const handleSuggestionSelect = useCallback((suggestion: RescheduleSuggestionDTO) => {
      setPendingSuggestion(suggestion);
    }, []);

    const handleConfirmationClose = useCallback(() => {
      if (isConfirming) {
        return;
      }

      setPendingSuggestion(null);
    }, [isConfirming]);

    const handleConfirmationConfirm = useCallback(async () => {
      if (!pendingSuggestion || !progress?.id) {
        return;
      }

      setIsConfirming(true);

      try {
        await axios.post(endpoints.activities.reschedule, {
          rescheduleProgressId: progress.id,
          solutionId: pendingSuggestion.solutionId,
        });

        await onRescheduleConfirm?.();

        toast.success(t('activities:allocation.toast.success'));
        setPendingSuggestion(null);
        onClose?.();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : '';
        toast.error(
          message
            ? t(`activities:allocation.toast.error.${message}`, {
                defaultValue: t('shuffle:stepResults.confirmError'),
              })
            : t('shuffle:stepResults.confirmError')
        );
      } finally {
        setIsConfirming(false);
      }
    }, [onClose, onRescheduleConfirm, pendingSuggestion, progress?.id, t]);

    const progressCurrent = progress?.completed ?? 0;
    const progressTotal = progress?.total;
    const progressValue =
      progress && progressTotal === 0 && progressCurrent === 0 ? 100 : undefined;
    const showResultsSkeleton =
      (isLoading || Boolean(progress && isRescheduleProgressRunning(progress.status))) &&
      suggestions.length === 0;

    return (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ProcessingProgress
              value={progressValue}
              current={progressCurrent}
              total={progressTotal === undefined ? 1 : progressTotal}
            />
          {showResultsSkeleton ? <InspectorAvailabilityGridSkeletonLoading /> : null}

          {orderedSuggestions.map(({ suggestion, isLoading: isSuggestionLoading }) => (
            <InspectorRescheduleCard
              key={suggestion.solutionId}
              suggestion={suggestion}
              currentActivity={currentActivity}
              isLoading={isSuggestionLoading}
              isDeskService={isDeskService}
              onSelect={handleSuggestionSelect}
              siteTimezoneId={siteTimezoneId}
              onDismiss={onDismiss}
            />
          ))}

          <RescheduleConfirmationModal
            open={Boolean(pendingSuggestion)}
            steps={confirmationSteps}
            isSubmitting={isConfirming}
            onClose={handleConfirmationClose}
            onConfirm={handleConfirmationConfirm}
          />

          {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" size="small" onClick={onClose} sx={{ minWidth: 64, height: 30 }}>
              {t('stepResults.close')}
            </Button>
          </Box> */}
        </Box>
      </>
    );
  }
);

StepResults.displayName = 'StepResults';
